/**
 * Zustand store cho trạng thái RPS tournament.
 * Tách riêng khỏi gameStore (dành cho quiz).
 */
import { create } from 'zustand';

export type RPSChoice = 'rock' | 'paper' | 'scissors';
export type MatchFormat = 'best_of_1' | 'best_of_3' | 'best_of_5';

export type RPSParticipantInfo = {
  participant_id: string;
  display_name: string | null;
  avatar_url: string | null;
};

export type RPSMatchState = {
  match_id: string;
  player1: RPSParticipantInfo;
  player2: RPSParticipantInfo | null;
  is_bye: boolean;
  match_format: MatchFormat;
  player1_games_won: number;
  player2_games_won: number;
  current_sub_round: number;
  status: 'pending' | 'in_progress' | 'completed';
  winner_id: string | null;
  // ephemeral: ai đã submit (server gửi player_ready)
  ready_player_ids: string[];
  // cho UI reveal animation
  last_reveal: {
    sub_round: number;
    player1_choice: RPSChoice;
    player2_choice: RPSChoice;
    result: 'player1_wins' | 'player2_wins' | 'draw';
    is_random_tiebreak: boolean;
  } | null;
};

export type TournamentFinalStanding = {
  participant_id: string;
  display_name: string | null;
  avatar_url: string | null;
  eliminated_in_round: number | null;
};

export type RPSPhase =
  | 'idle'
  | 'drawing'            // đang bốc thăm — hiển thị loading animation
  | 'bracket_ready'      // bracket hiển thị, chờ admin bấm "Bắt đầu thi đấu"
  | 'round_active'       // các trận đang diễn ra
  | 'round_transitioning' // vòng kết thúc, chờ admin bắt đầu vòng tiếp
  | 'tournament_ended';

type RPSState = {
  phase: RPSPhase;
  tournament_round: number;
  round_label: string;          // "Tứ kết", "Bán kết", "Chung kết"...
  participant_count: number;    // số người tham gia vòng hiện tại
  initial_participant_count: number; // tổng số người ban đầu (không giảm) — dùng để tính totalRounds
  bracketHistory: Record<number, RPSMatchState[]>; // lịch sử các vòng đã đấu (key = tournament_round)
  timer_sec: number;
  match_format: MatchFormat;
  matches: RPSMatchState[];
  /** match_id mà user đang spotlight */
  spotlight_match_id: string | null;
  /** match_id của chính user (null nếu bị loại / spectator) */
  my_match_id: string | null;
  my_choice_submitted: boolean;
  /** thời điểm sub_round hiện tại bắt đầu (epoch ms) */
  sub_round_started_at: number | null;
  sub_round_timer_sec: number;

  // round transition
  advancing_ids: string[];
  eliminated_ids: string[];
  next_round_starts_in_sec: number | null;

  // end state
  champion_id: string | null;
  final_standings: TournamentFinalStanding[];

  // Actions
  setBracketDrawStarted: (data: {
    tournament_round: number;
    round_label: string;
    participant_count: number;
  }) => void;

  setTournamentRoundStarted: (data: {
    tournament_round: number;
    round_label?: string;       // optional — provided when bracket_draw_started was skipped
    participant_count?: number; // optional — provided when bracket_draw_started was skipped
    matches: Array<{
      match_id: string;
      player1: RPSParticipantInfo;
      player2: RPSParticipantInfo | null;
      is_bye: boolean;
      match_format: MatchFormat;
    }>;
    timer_sec: number;
    match_format: MatchFormat;
    myParticipantId: string | null;
  }) => void;

  setSubRoundStarted: (data: {
    match_id: string;
    sub_round: number;
    started_at: number;
    timer_sec: number;
    player1_games_won: number;
    player2_games_won: number;
  }) => void;

  setPlayerReady: (data: { match_id: string; participant_id: string }) => void;

  setSubRoundRevealed: (data: {
    match_id: string;
    sub_round: number;
    player1_choice: RPSChoice;
    player2_choice: RPSChoice;
    result: 'player1_wins' | 'player2_wins' | 'draw';
    is_random_tiebreak: boolean;
  }) => void;

  setMatchCompleted: (data: {
    match_id: string;
    winner_id: string;
    loser_id: string;
    player1_games_won: number;
    player2_games_won: number;
    games_played: number;
    was_tiebreak: boolean;
  }) => void;

  setTournamentRoundCompleted: (data: {
    tournament_round: number;
    advancing_participant_ids: string[];
    eliminated_participant_ids: string[];
  }) => void;

  setTournamentEnded: (data: {
    champion_id: string;
    final_standings: TournamentFinalStanding[];
  }) => void;

  setSpotlightMatch: (matchId: string) => void;
  setMyChoiceSubmitted: () => void;
  reset: () => void;
};

export type { RPSState };

const initialState = {
  phase: 'idle' as RPSPhase,
  tournament_round: 0,
  round_label: '',
  participant_count: 0,
  initial_participant_count: 0,
  bracketHistory: {} as Record<number, RPSMatchState[]>,
  timer_sec: 10,
  match_format: 'best_of_1' as MatchFormat,
  matches: [] as RPSMatchState[],
  spotlight_match_id: null,
  my_match_id: null,
  my_choice_submitted: false,
  sub_round_started_at: null,
  sub_round_timer_sec: 10,
  advancing_ids: [] as string[],
  eliminated_ids: [] as string[],
  next_round_starts_in_sec: null as number | null,
  champion_id: null,
  final_standings: [] as TournamentFinalStanding[],
};

export const rpsStore = create<RPSState>((set, get) => ({
  ...initialState,

  setBracketDrawStarted: ({ tournament_round, round_label, participant_count }) => {
    set((prev) => ({
      phase: 'drawing',
      tournament_round,
      round_label,
      participant_count,
      initial_participant_count: Math.max(prev.initial_participant_count, participant_count),
      // Lưu vòng trước vào lịch sử trước khi xoá matches
      bracketHistory: prev.matches.length > 0
        ? { ...prev.bracketHistory, [prev.tournament_round]: prev.matches }
        : prev.bracketHistory,
      matches: [],
      spotlight_match_id: null,
      my_match_id: null,
      my_choice_submitted: false,
      advancing_ids: [],
      eliminated_ids: [],
    }));
  },

  setTournamentRoundStarted: ({ tournament_round, round_label, participant_count, matches, timer_sec, match_format, myParticipantId }) => {
    const matchStates: RPSMatchState[] = matches.map((m) => ({
      match_id: m.match_id,
      player1: m.player1,
      player2: m.player2,
      is_bye: m.is_bye,
      match_format: m.match_format,
      player1_games_won: 0,
      player2_games_won: 0,
      current_sub_round: 1,
      status: 'in_progress',
      winner_id: null,
      ready_player_ids: [],
      last_reveal: null,
    }));

    // Tìm match của người dùng
    let my_match_id: string | null = null;
    if (myParticipantId) {
      const myMatch = matchStates.find(
        (m) =>
          m.player1.participant_id === myParticipantId ||
          m.player2?.participant_id === myParticipantId,
      );
      my_match_id = myMatch?.match_id ?? null;
    }

    set((prev) => {
      // Nếu bracket_draw_started bị bỏ qua (chung kết), lưu vòng trước vào history tại đây
      const historyUpdate =
        prev.phase !== 'drawing' && prev.matches.length > 0 && !prev.bracketHistory[prev.tournament_round]
          ? { [prev.tournament_round]: prev.matches }
          : {};

      const newParticipantCount = participant_count ?? prev.participant_count;
      return {
        phase: 'bracket_ready',
        tournament_round,
        // Dùng round_label từ event nếu drawing bị skip, ngược lại giữ lại từ draw_started
        round_label: round_label ?? prev.round_label,
        participant_count: newParticipantCount,
        initial_participant_count: Math.max(prev.initial_participant_count, newParticipantCount),
        bracketHistory: { ...prev.bracketHistory, ...historyUpdate },
        timer_sec,
        match_format,
        matches: matchStates,
        spotlight_match_id: my_match_id ?? (matchStates[0]?.match_id ?? null),
        my_match_id,
        my_choice_submitted: false,
        sub_round_started_at: null,
        advancing_ids: [],
        eliminated_ids: [],
        next_round_starts_in_sec: null,
      };
    });
  },

  setSubRoundStarted: ({ match_id, sub_round, started_at, timer_sec, player1_games_won, player2_games_won }) => {
    set((state) => ({
      // Chuyển sang round_active khi game đầu tiên bắt đầu (từ bracket_ready)
      phase: state.phase === 'bracket_ready' ? 'round_active' : state.phase,
      sub_round_started_at: started_at,
      sub_round_timer_sec: timer_sec,
      my_choice_submitted: false,
      matches: state.matches.map((m) =>
        m.match_id === match_id
          ? {
              ...m,
              current_sub_round: sub_round,
              player1_games_won,
              player2_games_won,
              ready_player_ids: [],
              last_reveal: null,
              status: 'in_progress',
            }
          : m,
      ),
    }));
  },

  setPlayerReady: ({ match_id, participant_id }) => {
    set((state) => ({
      matches: state.matches.map((m) =>
        m.match_id === match_id
          ? { ...m, ready_player_ids: [...new Set([...m.ready_player_ids, participant_id])] }
          : m,
      ),
    }));
  },

  setSubRoundRevealed: ({ match_id, sub_round, player1_choice, player2_choice, result, is_random_tiebreak }) => {
    set((state) => ({
      matches: state.matches.map((m) =>
        m.match_id === match_id
          ? {
              ...m,
              last_reveal: { sub_round, player1_choice, player2_choice, result, is_random_tiebreak },
            }
          : m,
      ),
    }));
  },

  setMatchCompleted: ({ match_id, winner_id, player1_games_won, player2_games_won }) => {
    set((state) => ({
      matches: state.matches.map((m) =>
        m.match_id === match_id
          ? { ...m, status: 'completed', winner_id, player1_games_won, player2_games_won }
          : m,
      ),
    }));
  },

  setTournamentRoundCompleted: ({ advancing_participant_ids, eliminated_participant_ids }) => {
    set({
      phase: 'round_transitioning',
      advancing_ids: advancing_participant_ids,
      eliminated_ids: eliminated_participant_ids,
      next_round_starts_in_sec: null,
    });
  },

  setTournamentEnded: ({ champion_id, final_standings }) => {
    set({ phase: 'tournament_ended', champion_id, final_standings });
  },

  setSpotlightMatch: (matchId) => set({ spotlight_match_id: matchId }),

  setMyChoiceSubmitted: () => set({ my_choice_submitted: true }),

  reset: () => set(initialState),
}));

export function useRpsStore() {
  return rpsStore((s) => s);
}
